import React from "react";
import {
  FlaskConical,
  Rocket,
  Landmark,
  Footprints,
  Globe2,
  ArrowRight,
  ArrowUpRight,
  Award,
} from "lucide-react";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import AssociationCarousel from "./AssociationCarousel";
import {
  Container,
  Section,
  SectionHeading,
  Eyebrow,
  Card,
  Button,
  Stat,
  Reveal,
} from "../Components/UI";

const credentials = [
  "Founder & CEO, DevAnanta R&D",
  "BRICS 2026 Delegate",
  "Ex–Mercedes-Benz R&D",
  "ISRO Researcher",
];

const stats = [
  { value: "₹25 L", label: "Govt. grant — Elevate 2025", accent: "gold" as const },
  { value: "12 t", label: "CO₂ cut every year", accent: "brand" as const },
  { value: "8+", label: "BRICS nations engaged", accent: "brand" as const },
  { value: "180+ km", label: "Walked for the planet", accent: "brand" as const },
];

const pillars = [
  {
    Icon: FlaskConical,
    title: "Researcher",
    text: "From ISRO labs to an IEEE paper — turning deep science into real technology.",
  },
  {
    Icon: Rocket,
    title: "Entrepreneur",
    text: "Founder of DevAnanta R&D, building India's clean-energy future.",
  },
  {
    Icon: Landmark,
    title: "Policy",
    text: "Working with ministries and global forums to turn ideas into impact.",
  },
  {
    Icon: Footprints,
    title: "Social impact",
    text: "180+ km on foot across villages for clean air, trees and dignity.",
  },
  {
    Icon: Globe2,
    title: "International",
    text: "India's delegate at BRICS 2026, among 8+ nations shaping the future.",
  },
];

const recognitions = [
  {
    img: "/img/mybharat-1.jpg",
    pos: "object-center",
    badge: "2026 · New Delhi",
    title: "MY Bharat — Young Innovator & Entrepreneur",
    text: "Honoured at the Youth for Viksit Bharat Convention, in the presence of national leaders.",
  },
  {
    img: "/img/award-earthday.jpg",
    pos: "object-center",
    badge: "2026 · Global",
    title: "EarthDay.org — Our Power, Our Planet Stars",
    text: "First international honour, alongside ITC, Jio and the Indian Coast Guard.",
  },
  {
    img: "/img/elevate.jpg",
    pos: "object-top",
    badge: "2025 · Karnataka",
    title: "Elevate 2025 — Govt. of Karnataka",
    text: "Deep-tech award and a grant of up to ₹25 lakh from the State Government.",
  },
  {
    img: "/img/brics-panel.jpg",
    pos: "object-center",
    badge: "2026 · Indore",
    title: "BRICS Youth Entrepreneurship Delegate",
    text: "One of five delegates representing India among 8+ nations.",
  },
];

const Home: React.FC = () => {
  return (
    <main className="bg-ink-950">
      <Navbar />

      {/* ---------- HERO ---------- */}
      <section className="relative overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(60% 55% at 78% 18%, rgba(16,185,129,0.18), transparent 70%), radial-gradient(45% 40% at 10% 90%, rgba(16,185,129,0.10), transparent 70%)",
          }}
          aria-hidden
        />
        <Container className="relative grid min-h-screen items-center gap-12 pb-16 pt-28 lg:grid-cols-2 lg:gap-8 lg:pt-20">
          <Reveal>
            <p className="text-2xl text-zinc-400">नमस्ते, I'm</p>
            <h1 className="mt-2 font-display text-5xl font-bold leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl">
              Devyansh
              <br />
              Agrawal
            </h1>
            <p className="mt-6 max-w-md text-lg leading-relaxed text-zinc-300">
              Today's waste is tomorrow's power. I'm building clean, homegrown
              energy that frees India from imported fuel — and lets our planet
              breathe.
            </p>

            <div className="mt-7 flex flex-wrap gap-2">
              {credentials.map((c) => (
                <span
                  key={c}
                  className="rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs font-medium text-zinc-300"
                >
                  {c}
                </span>
              ))}
            </div>

            <div className="mt-9 flex flex-wrap gap-3">
              <Button href="/recognition">
                Explore my work <ArrowRight className="h-4 w-4" />
              </Button>
              <Button href="https://devananta.com/" variant="ghost" external>
                Visit DevAnanta <ArrowUpRight className="h-4 w-4" />
              </Button>
            </div>
          </Reveal>

          <Reveal delay={150} className="flex flex-col items-center gap-7">
            <div className="relative">
              <div className="absolute -inset-6 rounded-full bg-brand-500/20 blur-3xl" aria-hidden />
              <img
                src="/devyansh.png"
                alt="Devyansh Agrawal"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="relative z-10 h-[420px] w-auto object-contain sm:h-[520px] lg:h-[600px]"
              />
              <Card className="absolute bottom-6 left-0 z-20 flex items-center gap-3 px-4 py-3 shadow-card">
                <span className="grid h-9 w-9 place-items-center rounded-full bg-gold-500/15 text-gold-400">
                  <Award className="h-5 w-5" />
                </span>
                <span className="text-xs leading-tight text-zinc-200">
                  <strong className="block text-white">EarthDay.org</strong>
                  Global Stars 2026
                </span>
              </Card>
            </div>
            <p className="text-center text-[11px] font-medium uppercase tracking-[0.15em] text-zinc-400 sm:text-sm sm:tracking-[0.28em]">
              Scientist · Entrepreneur · Changemaker
            </p>
          </Reveal>
        </Container>
      </section>

      {/* ---------- STATS ---------- */}
      <Section className="!py-14">
        <Container>
          <Reveal>
            <Card className="grid grid-cols-2 gap-8 px-6 py-10 sm:px-10 lg:grid-cols-4">
              {stats.map((s) => (
                <Stat key={s.label} {...s} />
              ))}
            </Card>
          </Reveal>
        </Container>
      </Section>

      {/* ---------- MISSION ---------- */}
      <Section className="!pt-8">
        <Container>
          <Reveal>
            <SectionHeading
              eyebrow="The mission"
              title={<>What if waste could power India?</>}
              intro="India spends over $100 billion a year importing energy. I'm working to change that — turning industrial waste into powerful, affordable batteries, without the rare imported metals the world depends on. Clean energy, made in India, for everyone."
            />
          </Reveal>
        </Container>
      </Section>

      {/* ---------- PILLARS ---------- */}
      <Section className="!pt-4">
        <Container>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {pillars.map((p, i) => (
              <Reveal key={p.title} delay={i * 80}>
                <Card className="h-full p-6 transition-colors hover:border-brand-500/40">
                  <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-500/10 text-brand-400">
                    <p.Icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-semibold text-white">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-400">
                    {p.text}
                  </p>
                </Card>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      {/* ---------- RECOGNITION ---------- */}
      <Section id="recognition" className="bg-ink-900">
        <Container>
          <Reveal>
            <SectionHeading
              eyebrow="Recognition"
              title="From Karnataka to the global stage"
              intro="A few moments that mean the most — and the proof that this mission resonates far beyond one lab."
            />
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {recognitions.map((r, i) => (
              <Reveal key={r.title} delay={i * 90}>
                <Card className="group h-full overflow-hidden">
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={r.img}
                      alt={r.title}
                      loading="lazy"
                      decoding="async"
                      className={`h-full w-full object-cover ${r.pos} transition-transform duration-700 group-hover:scale-105`}
                    />
                    <span className="absolute left-3 top-3 rounded-full bg-ink-950/80 px-3 py-1 text-[11px] font-semibold text-gold-400 backdrop-blur">
                      {r.badge}
                    </span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-base font-semibold leading-snug text-white">
                      {r.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-zinc-400">
                      {r.text}
                    </p>
                  </div>
                </Card>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Button href="/recognition" variant="ghost">
              See all recognition & impact <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </Container>
      </Section>

      {/* ---------- DEVANANTA SPOTLIGHT ---------- */}
      <Section>
        <Container>
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <Reveal className="order-2 lg:order-1">
              <Eyebrow>My venture</Eyebrow>
              <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                DevAnanta R&D
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-zinc-300">
                A deep-tech startup building India's next-generation, rare-earth-free
                energy storage from waste-derived nanomaterials — efficient, scalable
                and safe.
              </p>
              <p className="mt-4 leading-relaxed text-zinc-400">
                Our goal is simple: make clean power affordable for every home,
                hospital, farm and soldier — and free India from imported batteries.
              </p>
              <p className="mt-6 text-sm text-zinc-500">
                Incubated at <span className="text-zinc-300">IIT Dharwad</span> ·
                Nirmaan, <span className="text-zinc-300">IIT Madras</span> · Supported
                by <span className="text-zinc-300">ARCI</span>, {" "}
                <span className="text-zinc-300">IKP</span> &{" "}
                <span className="text-zinc-300">iHub</span>
              </p>
              <div className="mt-8">
                <Button href="https://devananta.com/" external>
                  Explore DevAnanta <ArrowUpRight className="h-4 w-4" />
                </Button>
              </div>
            </Reveal>
            <Reveal delay={120} className="order-1 lg:order-2">
              <div className="overflow-hidden rounded-2xl border border-white/10">
                <img
                  src="/img/award-earthday2.jpg"
                  alt="DevAnanta R&D recognised on stage"
                  loading="lazy"
                  decoding="async"
                  className="aspect-[4/3] w-full object-cover"
                />
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>

      <AssociationCarousel />

      {/* ---------- CTA ---------- */}
      <Section>
        <Container>
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl border border-brand-500/20 bg-gradient-to-br from-brand-600/20 via-ink-900 to-ink-900 px-6 py-16 text-center sm:px-12">
              <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Let's build a cleaner, self-reliant future — together.
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-zinc-300">
                Open to deep-tech partnerships, climate-tech collaborations and
                investor conversations.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Button href="/connect_me">
                  Get in touch <ArrowRight className="h-4 w-4" />
                </Button>
                <Button href="/recognition" variant="ghost">
                  See my journey
                </Button>
              </div>
            </div>
          </Reveal>
        </Container>
      </Section>

      <Footer />
    </main>
  );
};

export default Home;
